import { useState } from 'react';
import Image from 'next/image';

import { CloseOutline } from '@lyearn/icons';
import { Button, Stack, Typography } from '@lyearn/ui';
import { Modal } from '@/components/Modals';
import { ModalVariant } from '@/components/Modals/types';

interface ImageViewerModalProps {
  images: string[];
  isOpen: boolean;
  onClose: () => void;
}

const ImageViewerModal = ({ images, isOpen, onClose }: ImageViewerModalProps) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const currentImage = images[currentImageIndex];

  return (
    <Modal open={isOpen} showCloseIcon={false} variant={ModalVariant.FullScreen} onClose={onClose}>
      <Stack className="h-screen w-screen bg-bg-primary" direction="col">
        {/* Header */}
        <Stack
          alignItems="center"
          className="h-64 px-24 border-b border-border"
          justifyContent="between">
          <Typography variant="heading-s">
            {currentImageIndex + 1} / {images.length}
          </Typography>
          <Button StartIcon={CloseOutline} className="!p-0" variant="ghost" onClick={onClose} />
        </Stack>

        {/* Main Image */}
        <Stack alignItems="center" className="flex-1 p-24 overflow-hidden" justifyContent="center">
          <div className="relative w-full h-full max-h-[calc(100vh-200px)]">
            <Image
              fill
              priority
              alt="Image Viewer"
              className="!relative h-full w-full"
              sizes="(max-width: 1200px) 100vw"
              src={currentImage}
              style={{ objectFit: 'contain' }}
            />
          </div>
        </Stack>

        {/* Image Switcher */}
        <Stack
          alignItems="center"
          className="sticky bottom-0 h-136 border-t border-border bg-bg-primary"
          justifyContent="center">
          <Stack className="max-w-[1200px] w-full px-24">
            <Stack className="gap-16 overflow-x-auto py-16 no-scrollbar">
              {images.map((image, index) => (
                <Stack
                  key={index}
                  className={`cursor-pointer rounded-8 overflow-hidden relative w-[100px] h-[100px] flex-shrink-0 ${
                    currentImageIndex === index ? 'ring-2 ring-border-active' : ''
                  }`}
                  onClick={() => setCurrentImageIndex(index)}>
                  <Image
                    fill
                    alt={`Thumbnail ${index + 1}`}
                    sizes="100px"
                    src={image}
                    style={{ objectFit: 'contain' }}
                  />
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default ImageViewerModal;
